import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { on } from '@ember/modifier';
import { fn, hash } from '@ember/helper';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import {
  HdsPageHeader,
  HdsAlert,
  HdsTabs,
  HdsLinkInline,
  HdsFormTextInputField,
  HdsTable,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  FlexGrid,
  Typography,
  CodeBlock,
  DisclosureGuide,
} from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';
import { AWS_DISCLOSURE_GUIDES_TGW_ATTACHMENTS } from 'networks-common/utils/aws-connections';
import {
  CONTENT_TABS_LABEL_TERMINAL,
  CONTENT_TABS_LABEL_WEB_CONSOLE,
  PRODUCT_CONSUL,
  PRODUCT_VAULT,
} from 'networks-common/utils/content-tabs';
import templateString from 'core/utils/template-string';

let consulIngress = templateString`aws ec2 --region ${'region'} authorize-security-group-ingress --group-id ${'groupId'} --ip-permissions
IpProtocol=tcp,FromPort=8301,ToPort=8301,IpRanges='[{CidrIp=${'cidrBlock'}}]'
IpProtocol=udp,FromPort=8301,ToPort=8301,IpRanges='[{CidrIp=${'cidrBlock'}}]'
IpProtocol=tcp,FromPort=8301,ToPort=8301,UserIdGroupPairs='[{GroupId=${'groupId'}}]'
IpProtocol=udp,FromPort=8301,ToPort=8301,UserIdGroupPairs='[{GroupId=${'groupId'}}]'`;

let consulEgressAgentless = templateString`aws ec2 --region ${'region'} authorize-security-group-egress --group-id ${'groupId'} --ip-permissions
IpProtocol=tcp,FromPort=8300,ToPort=8300,IpRanges='[{CidrIp=${'cidrBlock'}}]'
IpProtocol=tcp,FromPort=8301,ToPort=8301,IpRanges='[{CidrIp=${'cidrBlock'}}]'
IpProtocol=udp,FromPort=8301,ToPort=8301,IpRanges='[{CidrIp=${'cidrBlock'}}]'
IpProtocol=tcp,FromPort=8301,ToPort=8301,UserIdGroupPairs='[{GroupId=${'groupId'}}]'
IpProtocol=udp,FromPort=8301,ToPort=8301,UserIdGroupPairs='[{GroupId=${'groupId'}}]'
IpProtocol=tcp,FromPort=80,ToPort=80,IpRanges='[{CidrIp=${'cidrBlock'}}]'
IpProtocol=tcp,FromPort=443,ToPort=443,IpRanges='[{CidrIp=${'cidrBlock'}}]'
IpProtocol=tcp,FromPort=8502,ToPort=8502,IpRanges='[{CidrIp=${'cidrBlock'}}]'`;

let vaultTemplate = templateString`aws ec2 --region ${'region'} authorize-security-group-egress --group-id ${'groupId_flag'} --ip-permissions
IpProtocol=tcp,FromPort=8200,ToPort=8200,IpRanges='[{CidrIp=${'cidrIp_tcp_port8200'}}]'`;

let createRouteTemplate = templateString`aws ec2 --region ${'region'} create-route --route-table-id ${'routeTableId'} --destination-cidr-block ${'cidrBlock'} --transit-gateway-id ${'tgwId'}`;

/**
 *
 * `tab` will be the selected tab;
 * @argument tab;
 * @type {String}
 *
 */
/**
 *
 * `product` such as consul or vault;
 * @argument product;
 * @type {String}
 *
 */
/**
 *
 * `model` will be a model containing the attachment detail;
 * @argument model;
 * @type {Object}
 *
 */
/**
 *
 * `PageHvnTransitGatewayAttachmentsConfigurationInstructions` renders the HVN TGW attachments Configuration Instructions page.
 *
 *
 * ```
 * <Page::HvnTransitGatewayAttachments::ConfigurationInstructions
 *   @model={{model}}
 *   @tab={{tab}}
 *   @product={{product}}
 * />
 * ```
 *
 * @class PageHvnTransitGatewayAttachmentsConfigurationInstructions
 *
 */
export default class PageHvnTransitGatewayAttachmentsConfigurationInstructions extends Component {
  @service router;

  tabs = {
    terminal: CONTENT_TABS_LABEL_TERMINAL,
    webConsole: CONTENT_TABS_LABEL_WEB_CONSOLE,
  };

  products = {
    consul: PRODUCT_CONSUL,
    vault: PRODUCT_VAULT,
  };

  disclosureGuides = AWS_DISCLOSURE_GUIDES_TGW_ATTACHMENTS;

  @tracked awsSecurityGroupId = '';
  @tracked awsRouteTableId = '';

  updateQueryParams = (queryParams = {}) => {
    return this.router.transitionTo(this.router.currentRouteName, {
      queryParams,
    });
  };

  get cidrBlock() {
    let { cidrBlock } = this.args.model.network;
    return cidrBlock;
  }

  get region() {
    let { region } = this.args.model.tgwAttachment.location.region;
    return region;
  }

  get createRouteCommand() {
    let routeTableId = '<TARGET_VPC_ROUTE_TABLE_ID>';
    if (isPresent(this.awsRouteTableId)) {
      routeTableId = this.awsRouteTableId;
    }
    let string = createRouteTemplate({
      region: this.region,
      cidrBlock: this.cidrBlock,
      routeTableId: routeTableId,
      tgwId: this.args.model.tgwAttachment.providerData.awsData.tgwId,
    });

    return string;
  }

  get consulAuthorizeSecurityGroupIngress() {
    let string = consulIngress({
      region: this.region,
      groupId: this.awsSecurityGroupId,
      cidrBlock: this.cidrBlock,
    });

    return string;
  }

  get consulAuthorizeSecurityGroupEgress() {
    let string = consulEgressAgentless({
      region: this.region,
      groupId: this.awsSecurityGroupId,
      cidrBlock: this.cidrBlock,
    });

    return string;
  }

  get vaultAuthorizeSecurityGroupIngress() {
    let awsSecurityGroupId = '<USERS_SG_ID>';
    if (isPresent(this.awsSecurityGroupId)) {
      awsSecurityGroupId = this.awsSecurityGroupId;
    }
    let string = vaultTemplate({
      region: this.region,
      groupId_flag: this.awsSecurityGroupId,
      cidrIp_tcp_port8200: this.cidrBlock,
      groupId_tcp_port8200: awsSecurityGroupId,
    });
    return string;
  }
  <template>
    {{! template-lint-disable no-bare-strings }}
    {{! this rule is disabled to allow for numbers in the table }}
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader data-test-transit-gateway-attachment-header as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.Title>
            {{t
              "components.page.hvn-transit-gateway-attachments.detail.instructions.default.title"
            }}
          </PH.Title>
          <PH.Description>
            {{t
              "components.page.hvn-transit-gateway-attachments.detail.instructions.default.message"
            }}
          </PH.Description>
        </HdsPageHeader>
      </:header>
      <:body>
        <div class="hvnsTransitGatewayAttachmentsConfigurationInstructions">
          <div
            class="hvnsTransitGatewayAttachmentsConfigurationInstructions__instructions"
            data-test-transit-gateway-attachment-configuration-instructions
          >
            <FlexGrid as |G|>
              <G.Item @xs="12" @sm="12" @md="12" @lg="12">
                <FlexGrid as |G|>
                  <G.Item @xs="12" @sm="12" @md="8" @lg="8">
                    <HdsAlert
                      @type="compact"
                      @color="neutral"
                      {{boxMargin "0 0 md 0"}}
                      data-test-transit-gateway-attachment-configuration-banner-message
                      as |A|
                    >
                      <A.Description>
                        {{t
                          "components.page.hvn-transit-gateway-attachments.detail.instructions.default.info"
                        }}
                      </A.Description>
                    </HdsAlert>
                    <Typography
                      class="hds-typography-body-200 hds-font-weight-regular"
                      data-test-transit-gateway-attachment-configuration-intro-message
                    >
                      {{t
                        "components.page.hvn-transit-gateway-attachments.detail.instructions.default.message"
                      }}
                    </Typography>
                  </G.Item>
                </FlexGrid>

                <HdsTabs as |T|>
                  <T.Tab
                    @isSelected={{eq @tab this.tabs.terminal.id}}
                    data-test-transit-gateway-attachment-terminal-tab
                    {{on
                      "click"
                      (fn
                        this.updateQueryParams (hash tab=this.tabs.terminal.id)
                      )
                    }}
                  >
                    {{t this.tabs.terminal.label}}
                  </T.Tab>
                  <T.Panel />

                  <T.Tab
                    @isSelected={{eq @tab this.tabs.webConsole.id}}
                    data-test-transit-gateway-attachment-web-console-tab
                    {{on
                      "click"
                      (fn
                        this.updateQueryParams
                        (hash tab=this.tabs.webConsole.id)
                      )
                    }}
                  >
                    {{t this.tabs.webConsole.label}}
                  </T.Tab>
                  <T.Panel />
                </HdsTabs>

                <div {{boxPadding "xs 0 0 0"}}>
                  <Typography
                    @variant="h3"
                    class="hds-typography-display-300 hds-font-weight-bold"
                    {{boxMargin "0 0 md 0"}}
                  >
                    {{t
                      "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.title"
                    }}
                    {{#if (eq @tab this.tabs.webConsole.id)}}
                      <HdsLinkInline
                        class="headerLink"
                        @href="https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/working-with-security-groups.html#updating-security-group-rules"
                        @icon="external-link"
                        @iconPosition="trailing"
                        @isHrefExternal={{true}}
                      >
                        {{t
                          "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.link"
                        }}
                      </HdsLinkInline>
                    {{/if}}
                  </Typography>
                  {{#if (eq @tab this.tabs.terminal.id)}}
                    <div {{boxPadding "0 0 sm 0"}}>
                      <FlexGrid as |G|>
                        <G.Item @xs="12" @sm="12" @md="8" @lg="8">
                          {{! template-lint-disable no-model-argument-in-route-templates }}
                          <Typography
                            @variant="body1"
                            class="hds-typography-body-200 hds-font-weight-regular"
                            {{boxMargin "0 0 md 0"}}
                          >
                            {{t
                              "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.terminal.description"
                              hvnId=@model.network.id
                              htmlSafe=true
                            }}
                          </Typography>
                        </G.Item>
                      </FlexGrid>
                    </div>
                    <form
                      class="hcp-hds-form"
                      aria-labelledby="transit-gateway-attachment-terminal-updating-security-groups"
                      data-test-transit-gateway-attachment-terminal-updating-security-groups-form
                    >
                      <FlexGrid as |G|>
                        <G.Item @xs="8" @sm="8" @md="8" @lg="6">
                          <HdsFormTextInputField
                            @id="awsSecurityGroupId"
                            @value={{this.awsSecurityGroupId}}
                            data-test-aws-security-group-id
                            as |F|
                          >
                            <F.Label>
                              {{t
                                "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.terminal.label"
                              }}
                            </F.Label>
                            <F.HelperText>
                              {{t
                                "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.terminal.help-text"
                              }}
                            </F.HelperText>
                          </HdsFormTextInputField>
                        </G.Item>
                      </FlexGrid>

                      <div {{boxPadding "sm 0 0 0"}}>
                        <HdsTabs
                          data-test-transit-gateway-attachment-terminal-codeblock-tabs
                          as |T|
                        >
                          <T.Tab
                            @icon="consul-color"
                            @isSelected={{eq @product this.products.consul.id}}
                            data-test-transit-gateway-attachment-terminal-consul-tab
                            {{on
                              "click"
                              (fn
                                this.updateQueryParams
                                (hash product=this.products.consul.id)
                              )
                            }}
                          >
                            {{t
                              "components.networks.content-tabs.consul-snippets.label"
                            }}
                          </T.Tab>
                          <T.Panel />

                          <T.Tab
                            @icon="vault"
                            @isSelected={{eq @product this.products.vault.id}}
                            data-test-transit-gateway-attachment-terminal-vault-tab
                            {{on
                              "click"
                              (fn
                                this.updateQueryParams
                                (hash product=this.products.vault.id)
                              )
                            }}
                          >
                            {{t
                              "components.networks.content-tabs.vault-snippets.label"
                            }}
                          </T.Tab>
                          <T.Panel />
                        </HdsTabs>
                      </div>

                      {{#if (eq @product this.products.consul.id)}}
                        <div {{boxPadding "0 0 0 0"}}>
                          <Typography @variant="h4">
                            {{t
                              "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.inbound-rules"
                            }}
                          </Typography>
                          <CodeBlock
                            class="securityGroup"
                            data-test-transit-gateway-attachment-terminal-consul-codeblock-inbound
                          >
                            {{this.consulAuthorizeSecurityGroupIngress}}
                          </CodeBlock>
                        </div>
                        <div {{boxPadding "0 0 2xs 0"}}>
                          <Typography @variant="h4">
                            {{t
                              "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.outbound-rules"
                            }}
                          </Typography>
                          <CodeBlock
                            class="securityGroup"
                            data-test-transit-gateway-attachment-terminal-consul-codeblock-outbound
                          >
                            {{this.consulAuthorizeSecurityGroupEgress}}
                          </CodeBlock>
                        </div>
                      {{else if (eq @product this.products.vault.id)}}
                        <div {{boxPadding "0 0 2xs 0"}}>
                          <Typography @variant="h4">
                            {{t
                              "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.outbound-rules"
                            }}
                          </Typography>
                          <CodeBlock
                            class="securityGroup"
                            data-test-transit-gateway-attachment-terminal-vault-codeblock
                          >
                            {{this.vaultAuthorizeSecurityGroupIngress}}
                          </CodeBlock>
                        </div>
                      {{/if}}
                    </form>
                  {{else if (eq @tab this.tabs.webConsole.id)}}
                    <div {{boxPadding "0 0 lg 0"}}>
                      <FlexGrid as |G|>
                        <G.Item @xs="12" @sm="12" @md="8" @lg="8">
                          {{! template-lint-disable no-model-argument-in-route-templates }}
                          <Typography
                            class="hds-typography-body-200 hds-font-weight-regular"
                            {{boxMargin "0 0 lg 0"}}
                          >
                            {{t
                              "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.description"
                              hvnId=@model.network.id
                              htmlSafe=true
                            }}
                          </Typography>
                          <DisclosureGuide
                            @content={{this.disclosureGuides.updatingSecurityGroups}}
                          />
                        </G.Item>
                      </FlexGrid>
                    </div>

                    <HdsTabs
                      data-test-transit-gateway-attachment-web-console-rules-tabs
                      as |T|
                    >
                      <T.Tab
                        @icon="consul-color"
                        @isSelected={{eq @product this.products.consul.id}}
                        data-test-transit-gateway-attachment-web-console-consul-tab
                        {{on
                          "click"
                          (fn
                            this.updateQueryParams
                            (hash product=this.products.consul.id)
                          )
                        }}
                      >
                        {{t
                          "components.networks.content-tabs.consul-rules.label"
                        }}
                      </T.Tab>
                      <T.Panel />

                      <T.Tab
                        @icon="vault"
                        @isSelected={{eq @product this.products.vault.id}}
                        data-test-transit-gateway-attachment-web-console-vault-tab
                        {{on
                          "click"
                          (fn
                            this.updateQueryParams
                            (hash product=this.products.vault.id)
                          )
                        }}
                      >
                        {{t
                          "components.networks.content-tabs.vault-rules.label"
                        }}
                      </T.Tab>
                      <T.Panel />
                    </HdsTabs>

                    {{! table rules}}
                    {{#if (eq @product this.products.consul.id)}}
                      <div {{boxPadding "xs 0 sm 0"}}>
                        <Typography
                          @variant="h3"
                          class="hds-typography-display-300 hds-font-weight-bold"
                          {{boxMargin "0 0 md 0"}}
                        >
                          {{t
                            "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.inbound-rules"
                          }}
                        </Typography>
                        {{! INBOUND TABLE }}
                        <HdsTable
                          data-test-transit-gateway-attachment-web-console-inbound-rules-consul
                          @valign="middle"
                          @caption={{t
                            "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.caption"
                          }}
                        >
                          <:head as |H|>
                            <H.Tr>
                              <H.Th>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.headers.protocol"
                                }}
                              </H.Th>
                              <H.Th>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.headers.port-range"
                                }}
                              </H.Th>
                              <H.Th>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.headers.source"
                                }}
                              </H.Th>
                              <H.Th>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.headers.description"
                                }}
                              </H.Th>
                            </H.Tr>
                          </:head>
                          <:body as |B|>
                            <B.Tr data-test-list-ingress-item>
                              <B.Td>
                                <b>
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.tcp"
                                  }}
                                </b>
                              </B.Td>
                              <B.Td>
                                8301
                              </B.Td>
                              {{! template-lint-disable no-model-argument-in-route-templates }}
                              <B.Td>
                                {{@model.network.cidrBlock}}
                              </B.Td>
                              <B.Td>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.handle-gossip-from-server"
                                }}
                              </B.Td>
                            </B.Tr>

                            <B.Tr data-test-list-ingress-item>
                              <B.Td>
                                <b>
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.udp"
                                  }}
                                </b>
                              </B.Td>
                              <B.Td>
                                8301
                              </B.Td>
                              {{! template-lint-disable no-model-argument-in-route-templates }}
                              <B.Td>
                                {{@model.network.cidrBlock}}
                              </B.Td>
                              <B.Td>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.handle-gossip-from-server"
                                }}
                              </B.Td>
                            </B.Tr>
                            <B.Tr data-test-list-ingress-item>
                              <B.Td>
                                <b>
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.tcp"
                                  }}
                                </b>
                              </B.Td>
                              <B.Td>
                                8301
                              </B.Td>
                              <B.Td>
                                {{#if this.awsSecurityGroupId}}
                                  {{this.awsSecurityGroupId}}
                                {{else}}
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.security-group-id"
                                  }}
                                {{/if}}
                              </B.Td>
                              <B.Td>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.handle-gossip-between-agents"
                                }}
                              </B.Td>
                            </B.Tr>
                            <B.Tr data-test-list-ingress-item>
                              <B.Td>
                                <b>
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.udp"
                                  }}
                                </b>
                              </B.Td>
                              <B.Td>
                                8301
                              </B.Td>
                              <B.Td>
                                {{#if this.awsSecurityGroupId}}
                                  {{this.awsSecurityGroupId}}
                                {{else}}
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.security-group-id"
                                  }}
                                {{/if}}
                              </B.Td>
                              <B.Td>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.handle-gossip-between-agents"
                                }}
                              </B.Td>
                            </B.Tr>
                          </:body>
                        </HdsTable>
                      </div>
                    {{/if}}

                    <div {{boxPadding "xs 0 2xl 0"}}>
                      <Typography
                        @variant="h3"
                        class="hds-typography-display-300 hds-font-weight-bold"
                        {{boxMargin "0 0 md 0"}}
                      >
                        {{t
                          "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.outbound-rules"
                        }}
                      </Typography>
                      {{! OUTBOUND TABLE }}
                      {{#if (eq @product this.products.consul.id)}}
                        <HdsTable
                          @valign="middle"
                          @caption={{t
                            "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.caption"
                          }}
                          data-test-transit-gateway-attachment-web-console-outbound-rules-consul
                        >
                          <:head as |H|>
                            <H.Tr>
                              <H.Th>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.headers.protocol"
                                }}
                              </H.Th>
                              <H.Th>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.headers.port-range"
                                }}
                              </H.Th>
                              <H.Th>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.headers.destination"
                                }}
                              </H.Th>
                              <H.Th>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.headers.description"
                                }}
                              </H.Th>
                            </H.Tr>
                          </:head>
                          <:body as |B|>
                            <B.Tr data-test-list-egress-item>
                              <B.Td>
                                <b>
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.tcp"
                                  }}
                                </b>
                              </B.Td>
                              <B.Td>
                                8300
                              </B.Td>
                              {{! template-lint-disable no-model-argument-in-route-templates }}
                              <B.Td>
                                {{@model.network.cidrBlock}}
                              </B.Td>
                              <B.Td>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.clients-to-talk-to-server"
                                }}
                              </B.Td>
                            </B.Tr>
                            <B.Tr data-test-list-egress-item>
                              <B.Td>
                                <b>
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.tcp"
                                  }}
                                </b>
                              </B.Td>
                              <B.Td>
                                8301
                              </B.Td>
                              {{! template-lint-disable no-model-argument-in-route-templates }}
                              <B.Td>
                                {{@model.network.cidrBlock}}
                              </B.Td>
                              <B.Td>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.gossip-with-server"
                                }}
                              </B.Td>
                            </B.Tr>
                            <B.Tr data-test-list-egress-item>
                              <B.Td>
                                <b>
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.udp"
                                  }}
                                </b>
                              </B.Td>
                              <B.Td>
                                8301
                              </B.Td>
                              {{! template-lint-disable no-model-argument-in-route-templates }}
                              <B.Td>
                                {{@model.network.cidrBlock}}
                              </B.Td>
                              <B.Td>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.gossip-with-server"
                                }}
                              </B.Td>
                            </B.Tr>
                            <B.Tr data-test-list-egress-item>
                              <B.Td>
                                <b>
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.tcp"
                                  }}
                                </b>
                              </B.Td>
                              <B.Td>
                                8301
                              </B.Td>
                              <B.Td>
                                {{#if this.awsSecurityGroupId}}
                                  {{this.awsSecurityGroupId}}
                                {{else}}
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.security-group-id"
                                  }}
                                {{/if}}
                              </B.Td>
                              <B.Td>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.gossip-between-agents"
                                }}
                              </B.Td>
                            </B.Tr>
                            <B.Tr data-test-list-egress-item>
                              <B.Td>
                                <b>
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.udp"
                                  }}
                                </b>
                              </B.Td>
                              <B.Td>
                                8301
                              </B.Td>
                              <B.Td>
                                {{#if this.awsSecurityGroupId}}
                                  {{this.awsSecurityGroupId}}
                                {{else}}
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.security-group-id"
                                  }}
                                {{/if}}
                              </B.Td>
                              <B.Td>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.gossip-between-agents"
                                }}
                              </B.Td>
                            </B.Tr>
                            <B.Tr data-test-list-egress-item>
                              <B.Td>
                                <b>
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.http"
                                  }}
                                </b>
                              </B.Td>
                              <B.Td>
                                80
                              </B.Td>
                              {{! template-lint-disable no-model-argument-in-route-templates }}
                              <B.Td>
                                {{@model.network.cidrBlock}}
                              </B.Td>
                              <B.Td>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.http-api"
                                }}
                              </B.Td>
                            </B.Tr>
                            <B.Tr data-test-list-egress-item>
                              <B.Td>
                                <b>
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.https"
                                  }}
                                </b>
                              </B.Td>
                              <B.Td>
                                443
                              </B.Td>
                              {{! template-lint-disable no-model-argument-in-route-templates }}
                              <B.Td>
                                {{@model.network.cidrBlock}}
                              </B.Td>
                              <B.Td>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.https-api"
                                }}
                              </B.Td>
                            </B.Tr>
                          </:body>
                        </HdsTable>
                      {{else if (eq @product this.products.vault.id)}}
                        <HdsTable
                          @valign="middle"
                          @caption={{t
                            "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.caption"
                          }}
                          data-test-transit-gateway-attachment-web-console-outbound-rules-vault
                        >
                          <:head as |H|>
                            <H.Tr>
                              <H.Th>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.headers.protocol"
                                }}
                              </H.Th>
                              <H.Th>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.headers.port-range"
                                }}
                              </H.Th>
                              <H.Th>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.headers.destination"
                                }}
                              </H.Th>
                              <H.Th>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.headers.description"
                                }}
                              </H.Th>
                            </H.Tr>
                          </:head>
                          <:body as |B|>
                            <B.Tr data-test-list-egress-item>
                              <B.Td>
                                <b>
                                  {{t
                                    "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.tcp"
                                  }}
                                </b>
                              </B.Td>
                              <B.Td>
                                8200
                              </B.Td>
                              {{! template-lint-disable no-model-argument-in-route-templates }}
                              <B.Td>
                                {{@model.network.cidrBlock}}
                              </B.Td>
                              <B.Td>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-groups.web-console.table.labels.https-api"
                                }}
                              </B.Td>
                            </B.Tr>
                          </:body>
                        </HdsTable>
                      {{/if}}
                    </div>

                    {{! end of table rules}}
                  {{/if}}

                  <Typography
                    @variant="h3"
                    class="hds-typography-display-300 hds-font-weight-bold"
                    {{boxMargin "0 0 md 0"}}
                  >
                    {{t
                      "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-route-table.title"
                    }}
                    <HdsLinkInline
                      class="headerLink"
                      @href="https://docs.aws.amazon.com/vpc/latest/userguide/WorkWithRouteTables.html#AddRemoveRoutes"
                      @icon="external-link"
                      @iconPosition="trailing"
                      @isHrefExternal={{true}}
                    >
                      {{t
                        "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-route-table.web-console.link"
                      }}
                    </HdsLinkInline>
                  </Typography>

                  {{#if (eq @tab this.tabs.terminal.id)}}
                    <div {{boxPadding "0 0 sm 0"}}>
                      <FlexGrid as |G|>
                        <G.Item @xs="12" @sm="12" @md="8" @lg="8">
                          <Typography
                            @variant="body1"
                            class="hds-typography-body-200 hds-font-weight-regular"
                            {{boxMargin "0 0 md 0"}}
                          >
                            {{t
                              "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-route-table.terminal.description"
                              htmlSafe=true
                            }}
                          </Typography>
                        </G.Item>
                      </FlexGrid>
                    </div>
                    <form
                      class="hcp-hds-form"
                      aria-labelledby="transit-gateway-attachment-terminal-updating-routing-table"
                      data-test-transit-gateway-attachment-terminal-updating-routing-table-form
                    >
                      <FlexGrid as |G|>
                        <G.Item @xs="8" @sm="8" @md="8" @lg="4">
                          <HdsFormTextInputField
                            @id="awsRouteTableId"
                            @value={{this.awsRouteTableId}}
                            data-test-aws-route-table-id
                            as |F|
                          >
                            <F.Label>
                              {{t
                                "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-route-table.terminal.label"
                              }}
                            </F.Label>
                            <F.HelperText>
                              {{t
                                "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-route-table.terminal.help-text"
                              }}
                            </F.HelperText>
                          </HdsFormTextInputField>
                        </G.Item>
                      </FlexGrid>
                      <CodeBlock>
                        {{this.createRouteCommand}}
                      </CodeBlock>
                    </form>
                  {{else if (eq @tab this.tabs.webConsole.id)}}
                    <div {{boxPadding "0 0 lg 0"}}>
                      <FlexGrid as |G|>
                        <G.Item @xs="12" @sm="12" @md="8" @lg="8">
                          <Typography
                            class="hds-typography-body-200 hds-font-weight-regular"
                            {{boxMargin "0 0 lg 0"}}
                          >
                            {{t
                              "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-route-table.web-console.message"
                            }}
                          </Typography>
                          <DisclosureGuide
                            @content={{this.disclosureGuides.updatingRouteTable}}
                          />
                        </G.Item>
                      </FlexGrid>
                    </div>
                    <FlexGrid as |G|>
                      <G.Item @xs="12" @sm="12" @md="8" @lg="8">
                        {{! ROUTE TABLE }}
                        <HdsTable
                          @valign="middle"
                          @caption={{t
                            "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-route-table.web-console.table.caption"
                          }}
                        >
                          <:head as |H|>
                            <H.Tr>
                              <H.Th>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-route-table.web-console.table.headers.destination"
                                }}
                              </H.Th>
                              <H.Th>
                                {{t
                                  "components.page.hvn-transit-gateway-attachments.detail.instructions.default.updating-route-table.web-console.table.headers.target"
                                }}
                              </H.Th>
                            </H.Tr>
                          </:head>
                          <:body as |B|>
                            <B.Tr data-test-list-item>
                              {{! template-lint-disable no-model-argument-in-route-templates }}
                              <B.Td>
                                {{@model.network.cidrBlock}}
                              </B.Td>
                              {{! template-lint-disable no-model-argument-in-route-templates }}
                              <B.Td>
                                {{@model.tgwAttachment.providerData.awsData.tgwId}}
                              </B.Td>
                            </B.Tr>
                          </:body>
                        </HdsTable>
                      </G.Item>
                    </FlexGrid>
                  {{/if}}
                </div>
              </G.Item>
            </FlexGrid>
          </div>
        </div>
      </:body>
    </CoreLayoutMain>
  </template>
}
